import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { getCategories } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { MaskedLines, Marquee, ArchReveal, Reveal } from "@/components/motion";
import { IMG, FALLBACK_IMG } from "@/lib/images";
import { ArrowRight, Gift } from "lucide-react";
import {
  WeeklyPicks, CampaignBanners, GiftTeaser, LifeMoments,
  StyleSets, StoresSection, UgcGrid, CorporateBand, TrustBadges,
} from "@/components/home/HomeSections";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden grain bg-shule-base">
      <motion.div style={{ y: yBg }} className="absolute inset-0" aria-hidden="true">
        <img src={IMG.hero_main} alt="" fetchpriority="high" onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
          className="w-full h-[115%] object-cover object-[72%_center]" />
        <div className="absolute inset-0 bg-gradient-to-r from-shule-base via-shule-base/80 to-shule-base/10" />
      </motion.div>
      <div className="container-shule relative py-20 sm:py-24 lg:py-36 min-h-[70vh] flex flex-col justify-center">
        <motion.span
          data-testid="hero-manifesto-badge"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="badge-tag inline-flex items-center gap-2 self-start"
        >
          Küçük Detaylar, Büyük Mutluluklar
        </motion.span>
        <h1 className="mt-6 font-serif font-light tracking-tight text-5xl sm:text-6xl lg:text-8xl leading-[1.05] text-shule-coffee">
          <MaskedLines lines={["Her Anında"]} />
          <span className="block overflow-hidden pb-[0.12em]">
            <motion.span
              className="block italic text-shule-goldhover"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.9, delay: 0.45, ease: [0.25, 1, 0.5, 1] }}
            >
              Sen.
            </motion.span>
          </span>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-6 text-base sm:text-lg text-shule-mocha leading-relaxed max-w-md"
        >
          Zarafeti günlük hayatının bir parçası yap. Shule; takı, saç aksesuarı ve hediye seçkileriyle her anına eşlik eder.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-8 flex flex-wrap gap-3"
        >
          <Link to="/kategori/tumu" data-testid="hero-explore-button" className="btn-primary-shule">
            Tüm Ürünleri Keşfet <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/hediye" data-testid="hero-gift-button" className="btn-outline-shule">
            Hediye Seç
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function EditorialMarquee() {
  const words = ["Daima Seninle", "Stil Seninle Daha Güzel", "Küçük Detaylar, Büyük Mutluluklar", "Her Anında Sen", "Shule"];
  return (
    <div data-testid="editorial-marquee" className="border-y border-shule-line bg-shule-cream/70 py-5 select-none">
      <Marquee>
        {words.map((w, i) => (
          <span key={i} className="inline-flex items-center">
            <span className="font-serif text-3xl sm:text-4xl font-light italic text-shule-coffee/80 px-6">{w}</span>
            <span className="w-2 h-2 rounded-full bg-shule-rose/70" />
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function ArchCategories() {
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
  }, []);

  return (
    <section data-testid="arch-categories-section" className="py-16 md:py-24">
      <div className="container-shule">
        <Reveal className="flex items-end justify-between gap-4 mb-10">
          <div>
            <p className="eyebrow">Kategoriler</p>
            <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-shule-coffee">Tarzına göre keşfet</h2>
          </div>
          <Link to="/kategori/tumu" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-shule-goldhover hover:underline underline-offset-4 min-h-[44px]">
            Tümünü Gör <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4 lg:gap-5">
          {categories.map((c, i) => (
            <ArchReveal key={c.slug} delay={i * 0.07}>
              <Link data-testid={`arch-category-${c.slug}`} to={`/kategori/${c.slug}`} className="group block">
                <div className="arch-card transition-shadow duration-500 group-hover:shadow-[0_24px_45px_-18px_rgba(38,33,31,0.22)]">
                  <img src={c.image} alt={c.name} loading="lazy" onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                    className="w-full aspect-[3/4] object-cover transition-transform duration-700 group-hover:scale-[1.06]" />
                </div>
                <p className="mt-3 text-center font-serif text-lg">{c.name}</p>
                <p className="text-center text-xs text-shule-goldhover font-semibold mt-0.5 inline-flex items-center gap-1 w-full justify-center">
                  Keşfet <ArrowRight className="w-3 h-3" />
                </p>
              </Link>
            </ArchReveal>
          ))}
          <ArchReveal delay={categories.length * 0.07}>
            <Link data-testid="arch-category-hediye" to="/hediye" className="group block">
              <div className="arch-card transition-shadow duration-500 group-hover:shadow-[0_24px_45px_-18px_rgba(38,33,31,0.22)]">
                <img src={IMG.ring_box} alt="Hediye" loading="lazy" onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                  className="w-full aspect-[3/4] object-cover transition-transform duration-700 group-hover:scale-[1.06]" />
              </div>
              <p className="mt-3 text-center font-serif text-lg inline-flex items-center gap-1.5 w-full justify-center">
                <Gift className="w-4 h-4 text-shule-rose" /> Hediye
              </p>
              <p className="text-center text-xs text-shule-goldhover font-semibold mt-0.5 inline-flex items-center gap-1 w-full justify-center">
                Keşfet <ArrowRight className="w-3 h-3" />
              </p>
            </Link>
          </ArchReveal>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const homeJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": "https://www.shuletaki.com.tr/#organization",
      name: "Shule",
      slogan: "Her Anında Sen",
      url: "https://www.shuletaki.com.tr/",
      logo: "https://www.shuletaki.com.tr/assets/img/logo.png",
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": "https://www.shuletaki.com.tr/#website",
      url: "https://www.shuletaki.com.tr/",
      name: "Shule",
      inLanguage: "tr-TR",
      publisher: { "@id": "https://www.shuletaki.com.tr/#organization" },
    },
  ];

  return (
    <>
      <Seo jsonLd={homeJsonLd} path="/" />
      <Hero />
      <ArchCategories />
      <WeeklyPicks />
      <LifeMoments />
      <GiftTeaser />
      <CampaignBanners />
      <StyleSets />
      <StoresSection />
      <UgcGrid />
      <TrustBadges />
      <CorporateBand />
    </>
  );
}
